import { Box, Typography, Container, IconButton } from '@mui/material';
import { useState, useEffect } from 'react';
import { X, ChevronLeft, ChevronRight, ArrowLeft } from 'lucide-react';
import AOS from 'aos';
import 'aos/dist/aos.css';
import projects from '../../data/projectsData';

function Gallery() {
  const [activeProject, setActiveProject] = useState(null); // null = grid view
  const [lightboxIndex, setLightboxIndex] = useState(null); // null = no lightbox

  useEffect(() => {
    AOS.init({ duration: 800, once: false, offset: 100, easing: 'ease-out-cubic' });
  }, []);

  // scroll to top when entering detail view
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [activeProject]);

  const openProject = (project) => setActiveProject(project);
  const closeProject = () => setActiveProject(null);

  const openLightbox = (index) => setLightboxIndex(index);
  const closeLightbox = () => setLightboxIndex(null);
  const prevImage = (e) => {
    e.stopPropagation();
    setLightboxIndex(i => (i > 0 ? i - 1 : activeProject.images.length - 1));
  };
  const nextImage = (e) => {
    e.stopPropagation();
    setLightboxIndex(i => (i < activeProject.images.length - 1 ? i + 1 : 0));
  };

  // keyboard nav
  useEffect(() => {
    if (lightboxIndex === null) return;
    const handler = (e) => {
      if (e.key === 'ArrowLeft') setLightboxIndex(i => (i > 0 ? i - 1 : activeProject.images.length - 1));
      if (e.key === 'ArrowRight') setLightboxIndex(i => (i < activeProject.images.length - 1 ? i + 1 : 0));
      if (e.key === 'Escape') closeLightbox();
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [lightboxIndex, activeProject]);

  return (
    <Box sx={{ minHeight: '100vh', backgroundColor: '#f8f8f8', overflow: 'hidden' }}>

      {/* ── PAGE HERO ── */}
      <Box sx={{
        backgroundColor: 'var(--primary-color)',
        py: { xs: 6, md: 9 },
        textAlign: 'center',
        position: 'relative',
        overflow: 'hidden',
        '&::before': {
          content: '""', position: 'absolute',
          top: '-80px', right: '-80px',
          width: '320px', height: '320px',
          borderRadius: '50%',
          border: '70px solid rgba(192,174,93,0.07)',
          pointerEvents: 'none',
        },
        '&::after': {
          content: '""', position: 'absolute',
          bottom: '-90px', left: '-50px',
          width: '280px', height: '280px',
          borderRadius: '50%',
          border: '55px solid rgba(192,174,93,0.05)',
          pointerEvents: 'none',
        },
      }}>
        <Typography sx={{ fontSize: { xs: '34px', md: '52px' }, fontWeight: 800, color: '#fff', letterSpacing: 2 }}>
          {activeProject ? activeProject.name.toUpperCase() : 'OUR PROJECTS'}
        </Typography>
        <Box sx={{ width: '80px', height: '4px', backgroundColor: 'var(--primary-text-color)', mx: 'auto', mt: 3, mb: 2 }} />
        <Typography sx={{ fontSize: { xs: '15px', md: '17px' }, color: 'rgba(255,255,255,0.7)', mt: 1.5 }}>
          {activeProject ? activeProject.location : 'Explore our portfolio of beautifully crafted homes'}
        </Typography>
      </Box>

      {/* ── PROJECT GRID VIEW ── */}
      {!activeProject && (
        <Container maxWidth="xl" sx={{ py: { xs: 5, md: 8 } }}>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: '24px' }}>
            {projects.map((project, index) => (
              <Box
                key={project.id}
                data-aos="fade-up"
                data-aos-delay={index * 50}
                onClick={() => openProject(project)}
                sx={{
                  width: { xs: '100%', sm: 'calc(50% - 12px)', md: 'calc(33.333% - 16px)' },
                  cursor: 'pointer',
                  borderRadius: '12px',
                  overflow: 'hidden',
                  backgroundColor: '#fff',
                  boxShadow: '0 2px 20px rgba(19,25,83,0.07)',
                  transition: 'transform 0.3s ease, box-shadow 0.3s ease',
                  '&:hover': {
                    transform: 'translateY(-6px)',
                    boxShadow: '0 10px 36px rgba(19,25,83,0.13)',
                    '& .card-img': { transform: 'scale(1.07)' },
                    '& .card-overlay': { opacity: 1 },
                    '& .card-border': { borderTopColor: 'var(--primary-text-color)' },
                  }
                }}
              >
                {/* Thumbnail */}
                <Box sx={{ position: 'relative', width: '100%', aspectRatio: '4/3', overflow: 'hidden', backgroundColor: '#ddd' }}>
                  <Box
                    component="img"
                    src={project.thumbnail}
                    alt={project.name}
                    loading="lazy"
                    className="card-img"
                    sx={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block', transition: 'transform 0.5s ease' }}
                  />
                  <Box className="card-overlay" sx={{
                    position: 'absolute', inset: 0,
                    background: 'linear-gradient(to top, rgba(19,25,83,0.8) 0%, transparent 55%)',
                    opacity: 0, transition: 'opacity 0.35s ease',
                    display: 'flex', alignItems: 'flex-end', p: 2.5
                  }}>
                    <Typography sx={{ color: '#fff', fontSize: '13px', fontWeight: 700, letterSpacing: 1.5, textTransform: 'uppercase', borderBottom: '2px solid var(--primary-text-color)', pb: '2px' }}>
                      View Project
                    </Typography>
                  </Box>
                </Box>

                {/* Card info */}
                <Box className="card-border" sx={{ p: '16px 20px', borderTop: '3px solid transparent', transition: 'border-top-color 0.35s ease' }}>
                  <Typography sx={{ fontSize: '17px', fontWeight: 700, color: 'var(--primary-color)', mb: 0.4 }}>
                    {project.name}
                  </Typography>
                  <Typography sx={{ fontSize: '13px', color: 'var(--black-light-color)' }}>
                    {project.images.length} photos
                  </Typography>
                </Box>
              </Box>
            ))}
          </Box>
        </Container>
      )}

      {/* ── PROJECT DETAIL VIEW ── */}
      {activeProject && (
        <Container maxWidth="xl" sx={{ py: { xs: 4, md: 7 } }}>

          {/* Back button */}
          <Box
            onClick={closeProject}
            sx={{
              display: 'inline-flex', alignItems: 'center', gap: 1,
              cursor: 'pointer', mb: 4,
              color: 'var(--primary-color)',
              fontWeight: 600, fontSize: '15px',
              '&:hover': { color: 'var(--primary-text-color)' },
              transition: 'color 0.3s ease'
            }}
          >
            <ArrowLeft size={20} />
            Back to All Projects
          </Box>

          {/* Hero image */}
          <Box sx={{
            width: '100%', aspectRatio: { xs: '4/3', md: '16/7' },
            borderRadius: '12px', overflow: 'hidden', mb: 5,
            boxShadow: '0 10px 36px rgba(19,25,83,0.13)'
          }}>
            <Box
              component="img"
              src={activeProject.thumbnail}
              alt={activeProject.name}
              sx={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
            />
          </Box>

          {/* Project title row */}
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 4, flexWrap: 'wrap', gap: 2 }}>
            <Box>
              <Typography sx={{ fontSize: { xs: '24px', md: '32px' }, fontWeight: 800, color: 'var(--primary-color)' }}>
                {activeProject.name}
              </Typography>
              <Typography sx={{ fontSize: '14px', color: 'var(--black-light-color)', mt: 0.5 }}>
                {activeProject.images.length} photos
              </Typography>
            </Box>
          </Box>

          {/* Gold divider */}
          <Box sx={{ width: '80px', height: '4px', backgroundColor: 'var(--primary-text-color)', mb: 5 }} />

          {/* Images grid */}
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: '16px' }}>
            {activeProject.images.map((src, index) => (
              <Box
                key={index}
                onClick={() => openLightbox(index)}
                data-aos="fade-up"
                data-aos-delay={(index % 6) * 40}
                sx={{
                  width: { xs: '100%', sm: 'calc(50% - 8px)', md: 'calc(33.333% - 11px)' },
                  aspectRatio: '4/3',
                  borderRadius: '12px',
                  overflow: 'hidden',
                  cursor: 'pointer',
                  position: 'relative',
                  boxShadow: '0 2px 10px rgba(0,0,0,0.1)',
                  transition: 'transform 0.3s ease, box-shadow 0.3s ease',
                  '&:hover': {
                    transform: 'translateY(-6px)',
                    boxShadow: '0 10px 36px rgba(19,25,83,0.13)',
                    '& .img-overlay': { opacity: 1 },
                    '& img': { transform: 'scale(1.06)' },
                  }
                }}
              >
                <Box
                  component="img"
                  src={src}
                  alt={`${activeProject.name} - ${index + 1}`}
                  loading="lazy"
                  sx={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block', transition: 'transform 0.4s ease' }}
                />
                <Box className="img-overlay" sx={{
                  position: 'absolute', inset: 0,
                  background: 'rgba(19,25,83,0.45)',
                  opacity: 0, transition: 'opacity 0.3s ease',
                  display: 'flex', alignItems: 'center', justifyContent: 'center'
                }}>
                  <Typography sx={{ color: '#fff', fontSize: '13px', fontWeight: 700, letterSpacing: 1.5, textTransform: 'uppercase' }}>
                    View Full
                  </Typography>
                </Box>
              </Box>
            ))}
          </Box>
        </Container>
      )}

      {/* ── LIGHTBOX ── */}
      {lightboxIndex !== null && activeProject && (
        <Box
          onClick={closeLightbox}
          sx={{
            position: 'fixed', inset: 0,
            backgroundColor: 'rgba(0,0,0,0.96)',
            zIndex: 9999,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            isolation: 'isolate'
          }}
        >
          {/* Close */}
          <IconButton onClick={closeLightbox} sx={{
            position: 'absolute', top: 16, right: 16, zIndex: 10001,
            backgroundColor: 'var(--primary-text-color)',
            '&:hover': { backgroundColor: '#fff' }
          }}>
            <X size={22} color="var(--primary-color)" />
          </IconButton>

          {/* Prev */}
          <IconButton onClick={prevImage} sx={{
            position: 'absolute', left: { xs: 8, md: 32 }, zIndex: 10001,
            backgroundColor: 'var(--primary-text-color)',
            '&:hover': { backgroundColor: '#fff' }
          }}>
            <ChevronLeft size={28} color="var(--primary-color)" />
          </IconButton>

          {/* Next */}
          <IconButton onClick={nextImage} sx={{
            position: 'absolute', right: { xs: 8, md: 32 }, zIndex: 10001,
            backgroundColor: 'var(--primary-text-color)',
            '&:hover': { backgroundColor: '#fff' }
          }}>
            <ChevronRight size={28} color="var(--primary-color)" />
          </IconButton>

          {/* Image */}
          <Box onClick={e => e.stopPropagation()} sx={{ maxWidth: '90vw', maxHeight: '90vh', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2 }}>
            <Box
              component="img"
              src={activeProject.images[lightboxIndex]}
              alt={`${activeProject.name} ${lightboxIndex + 1}`}
              sx={{
                maxWidth: '100%', maxHeight: '80vh',
                width: 'auto', height: 'auto',
                objectFit: 'contain',
                borderRadius: '6px',
                display: 'block',
                filter: 'none !important',
                WebkitFilter: 'none',
                mixBlendMode: 'normal',
                isolation: 'isolate',
                boxShadow: '0 20px 60px rgba(0,0,0,0.6)'
              }}
            />
            <Typography sx={{ color: 'rgba(255,255,255,0.7)', fontSize: '14px' }}>
              {lightboxIndex + 1} / {activeProject.images.length}
            </Typography>
          </Box>
        </Box>
      )}

    </Box>
  );
}

export default Gallery;
